import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { AriaRole, CSSProperties, memo } from 'react';

import { toRem } from '../../utils/ui';

import styles from './loader.module.scss';

export const Loader = memo((props: LoaderProps) => {
  const {
    size,
    error,
    absolute,
    progress,
    thin,
    glow,
    className,
    fullScreen,
    color = 'hero',
    variant = 'arc',
    type = 'spinner',
    ...rest
  } = props;

  const style = size ? ({ '--size': toRem(size) } as CSSProperties) : undefined;

  const renderArc = (fullScreen = false) => (
    <div
      className={clsx(styles.arcSpinner, className, {
        [styles.absolute]: absolute,
        [styles.fullScreen]: fullScreen,
      })}
      style={style}
      {...rest}
    >
      <div style={style} className={clsx(styles.spinner, styles[`c-${color}`])} />
    </div>
  );

  const renderSteps = () => (
    <div className={clsx(styles.stepsSpinner, styles[`c-${color}`], className)} style={style} {...rest}>
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} />
      ))}
    </div>
  );

  if (fullScreen) {
    return createPortal(renderArc(true), document.body);
  }

  if (type === 'spinner') {
    return variant === 'arc' ? renderArc() : renderSteps();
  }

  const progressStyle = { '--progress': `${progress || 0}%` } as CSSProperties;

  return (
    <div className={clsx(styles.progressBar, className, { [styles.thin]: thin, [styles.glowing]: glow })} {...rest}>
      <div
        className={clsx(styles.bar, styles[`c-${color}`], {
          [styles.error]: error,
        })}
        style={progressStyle}
      />
      {glow && <div aria-hidden="true" className={clsx(styles.glow, styles[`c-${color}`])} style={progressStyle} />}
    </div>
  );
});

export interface LoaderProps {
  role?: AriaRole;
  size?: number;
  /** A 4 tall progress bar with a 2 radius. */
  thin?: boolean;
  /** A blurred copy of the progress fill over it, for `type="progress"`. */
  glow?: boolean;
  error?: boolean;
  progress?: number;
  className?: string;
  absolute?: boolean;
  fullScreen?: boolean;
  variant?: 'arc' | 'steps';
  type?: 'spinner' | 'progress';
  color?: 'hero' | 'hero-sc' | 'warning' | 'danger' | 'light' | 'dark' | 'success-sc' | 'success';
}

export default Loader;
